import { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';

interface TeamMember {
  _id: string;
  name: string;
  email?: string;
  phone?: string;
  department: string;
  position?: string;
  teamName?: string;
  avatar?: string;
  isTeamLead: boolean;
}

export const TeamMembersPage = () => {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${import.meta.env.ADMIN_API_URL}/team-members`);
      setMembers(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    } finally {
      setLoading(false);
    }
  };

  const departments = ['all', ...Array.from(new Set(members.map(m => m.department)))];

  const filteredMembers = members.filter(member => {
    const matchesSearch = 
      member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.position?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.department.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDepartment = selectedDepartment === 'all' || member.department === selectedDepartment;
    
    return matchesSearch && matchesDepartment;
  });

  if (loading) {
    return (
      <Container>
        <LoadingSpinner>Loading team members...</LoadingSpinner>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Our Team</Title>
        <Subtitle>Meet the people who make it happen</Subtitle>
      </Header>

      <Controls>
        <SearchBar
          type="text"
          placeholder="Search by name, position, or department..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        
        <DepartmentFilter>
          {departments.map(dept => (
            <FilterButton
              key={dept}
              active={selectedDepartment === dept}
              onClick={() => setSelectedDepartment(dept)}
            >
              {dept === 'all' ? 'All Departments' : dept}
            </FilterButton>
          ))}
        </DepartmentFilter>
      </Controls>

      {filteredMembers.length === 0 ? (
        <EmptyState>
          <EmptyIcon>👥</EmptyIcon>
          <EmptyText>No team members found</EmptyText>
        </EmptyState>
      ) : (
        <Grid>
          {filteredMembers.map((member) => (
            <MemberCard key={member._id} isLead={member.isTeamLead}>
              {member.isTeamLead && <LeaderBadge>⭐ Team Leader</LeaderBadge>}
              
              <AvatarContainer>
                {member.avatar ? (
                  <Avatar src={member.avatar} alt={member.name} />
                ) : (
                  <AvatarPlaceholder>
                    {member.name.charAt(0).toUpperCase()}
                  </AvatarPlaceholder>
                )}
              </AvatarContainer>

              <MemberInfo>
                <MemberName>{member.name}</MemberName>
                {member.position && <Position>{member.position}</Position>}
                <Department>{member.department}</Department>
                {member.teamName && <TeamName>{member.teamName}</TeamName>}
                
                {member.email && (
                  <ContactInfo>
                    <Icon>📧</Icon>
                    <ContactText>{member.email}</ContactText>
                  </ContactInfo>
                )}
                
                {member.phone && (
                  <ContactInfo>
                    <Icon>📞</Icon>
                    <ContactText>{member.phone}</ContactText>
                  </ContactInfo>
                )}
              </MemberInfo>
            </MemberCard>
          ))}
        </Grid>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  color: white;
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 0.5rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  opacity: 0.9;
`;

const Controls = styled.div`
  margin-bottom: 2rem;
`;

const SearchBar = styled.input`
  width: 100%;
  padding: 1rem 1.5rem;
  font-size: 1.1rem;
  border: none;
  border-radius: 12px;
  background: white;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 1rem;
  
  &:focus {
    outline: none;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const DepartmentFilter = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const FilterButton = styled.button<{ active: boolean }>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.active ? 'white' : 'rgba(255, 255, 255, 0.2)'};
  color: ${props => props.active ? '#667eea' : 'white'};
  box-shadow: ${props => props.active ? '0 4px 12px rgba(0, 0, 0, 0.15)' : 'none'};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 2rem;
`;

const MemberCard = styled.div<{ isLead: boolean }>`
  background: ${props => props.isLead 
    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    : 'white'
  };
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  // position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.isLead ? '#ffd700' : '#667eea'};
  }
`;

const LeaderBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 215, 0, 0.9);
  color: #333;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;

const AvatarContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
`;

const Avatar = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const AvatarPlaceholder = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  font-weight: 700;
  color: white;
  border: 4px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const MemberInfo = styled.div`
  text-align: center;
`;

const MemberName = styled.h3<{ isLead?: boolean }>`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: ${props => props.isLead ? 'white' : '#333'};
`;

const Position = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #667eea;
  margin-bottom: 0.5rem;
`;

const Department = styled.div`
  display: inline-block;
  padding: 0.5rem 1rem;
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const TeamName = styled.div`
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 1rem;
  font-style: italic;
`;

const ContactInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
  font-size: 0.95rem;
  color: #555;
`;

const Icon = styled.span`
  font-size: 1.2rem;
`;

const ContactText = styled.span`
  word-break: break-all;
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60vh;
  font-size: 1.5rem;
  color: white;
  font-weight: 600;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: white;
`;

const EmptyIcon = styled.div`
  font-size: 5rem;
  margin-bottom: 1rem;
`;

const EmptyText = styled.p`
  font-size: 1.5rem;
  font-weight: 600;
`;
